// astriaMexicoTheme.ts
import { useEffect, useState } from "react";

// ─── Astria Mexico — light/dark palette. Warm, expressive, grounded per the
// backend's MX_TONE_MATRIX (helper/astriaMexicoService.js): terracotta/
// marigold accent against a soft warm-neutral background — distinct from the
// cool-gray UK V2 palette, matching the "cálido, cercano" voice.
//
// Scoped to the Mexico lane only (persisted under its own localStorage key)
// — doesn't touch any shared/global theme mechanism.

export const MX_HEADING_FONT = "'Georgia', 'Times New Roman', serif";
export const MX_BODY_FONT = "'Inter', sans-serif";

export interface MXPalette {
  bg: string;
  card: string;
  section: string;
  cardBorder: string;
  titleColor: string;
  sectionTitleColor: string;
  bodyColor: string;
  mutedColor: string;
  accent: string;
  accentSoft: string;
  accentGlow: string;
  highlight: string;
  highlightSoft: string;
  white: string;
  headerBg: string;
}

const LIGHT_PALETTE: MXPalette = {
  bg: "#FAF4EE",
  card: "#FFFFFF",
  section: "#F3E7DA",
  cardBorder: "#E8D6C3",
  titleColor: "#3A2A20",
  sectionTitleColor: "#3A2A20",
  bodyColor: "#4A3B30",
  mutedColor: "#8C7566",
  accent: "#C1502E",
  accentSoft: "rgba(193,80,46,0.10)",
  accentGlow: "0 0 0 3px rgba(193,80,46,0.16)",
  highlight: "#E4A335",
  highlightSoft: "rgba(228,163,53,0.18)",
  white: "#FFFFFF",
  headerBg: "rgba(250,244,238,0.85)",
};

const DARK_PALETTE: MXPalette = {
  bg: "#211714",
  card: "#2B201A",
  section: "#33261F",
  cardBorder: "#453428",
  titleColor: "#F3E7DA",
  sectionTitleColor: "#F3E7DA",
  bodyColor: "#E1D2C4",
  mutedColor: "#B29A87",
  accent: "#E27049",
  accentSoft: "rgba(226,112,73,0.14)",
  accentGlow: "0 0 0 3px rgba(226,112,73,0.22)",
  highlight: "#EFC15D",
  highlightSoft: "rgba(239,193,93,0.2)",
  white: "#FFFFFF",
  headerBg: "rgba(33,23,20,0.85)",
};

// Kept as a plain export (light palette) for any call site that doesn't need
// to react to the toggle — e.g. static color references outside a component.
export const SHELL_COLORS_MEXICO = LIGHT_PALETTE;

const STORAGE_KEY = "astriaMexicoTheme";

function getStoredTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
}

export function useMexicoTheme(): {
  theme: "light" | "dark";
  colors: MXPalette;
  toggleTheme: () => void;
} {
  const [theme, setTheme] = useState<"light" | "dark">(getStoredTheme);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return { theme, colors: theme === "dark" ? DARK_PALETTE : LIGHT_PALETTE, toggleTheme };
}
